<template>
  <div class="frameworkwheel">
    <center class="frameworkwheel__svg" :id="'frameworkwheel--' + this._uid">
      <svg :id="'frameworkwheel-svg--' + this._uid">
        <filter id="dropshadow" height="130%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="3"/> <!-- stdDeviation is how much to blur -->
          <feOffset dx="0" dy="5" result="offsetblur"/> <!-- how much to offset -->
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.15"/> <!-- slope is the opacity of the shadow -->
          </feComponentTransfer>
          <feMerge> 
            <feMergeNode/> <!-- this contains the offset blurred image -->
            <feMergeNode in="SourceGraphic"/> <!-- this contains the element that the filter is applied to -->
          </feMerge>
        </filter>
      </svg>
    </center>
  </div>
</template>

<script>
// import Vue from 'vue'
import SVG from 'svg.js'
import 'svg.filter.js'
import _ from 'lodash'
import CircleCalculations from './CircleCalculations.js'
import splitText from './StrSplitter.js'
import { FrameworkWheelMixins, Covid19icons } from './../../covid19/FrameworkWheelMixins.js'

export default {
  name: 'frameworkwheel',
  mixins: [ Covid19icons, FrameworkWheelMixins ],
  props: {
    dataset: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      CircleCalculations: false,
      hasBeenAnimated: false,
      groups: [],
      canvas: false,
      canvasSize: [500, 500],
      widthHeight: [500, 500],
      attributeLabels: {
        A_01: [ 'SA_01_01', 'SA_01_02', 'SA_01_03', 'SA_01_04' ],
        A_02: [ 'SA_02_01', 'SA_02_02', 'SA_02_03' ],
        A_03: [ 'SA_03_01', 'SA_03_02', 'SA_03_03' ],
        A_04: [ 'SA_04_01', 'SA_04_02' ],
        A_05: [ 'SA_05_01', 'SA_05_02', 'SA_05_03', 'SA_05_04' ]
      }
    }
  },
  watch: {
    dataset () {
      this.reset()
      this.drawChart()
      this.setCovid19AttributeIcons()
    },
    canvasSize () {
      this.CircleCalculations.setCircleDimensions(
        this.canvasSize[0],
        this.canvasSize[1]
      )
    }
  },
  mounted () {
    this.CircleCalculations = new CircleCalculations()
    this.CircleCalculations.setCircleDimensions(
      this.canvasSize[0],
      this.canvasSize[1]
    )

    this.drawCanvas()
    this.drawChart()
    this.setCovid19AttributeIcons()
  },
  computed: {
    firstCountry () {
      return this.$store.getters['rsc/firstSelected']
    }
  },
  methods: {
    hasDataset () {
      return Object.keys(this.dataset).length > 0
    },
    /**
     * Reset the SVG
     *
     * @return  {void}
     */
    reset () {
      _.each(this.groups, (group) => {
        group.remove()
      })
      this.hasBeenAnimated = false
    },

    rotateCircle (circleObj, rotation) {
      let newRotation = -90 + rotation
      circleObj.rotate(newRotation, this.canvasSize[0] / 2, this.canvasSize[0] / 2)
    },

    /**
     * Draws a circle
     * @param  {Float}  value           The value
     * @param  {String}  className      ClassName
     * @param  {Boolean/Integer} speed  The animation speed - or false if no animatino
     * @return {Object}                 The svgjs circle object
     */
    drawCircle (value, className, position = 1, speed = false) {
      let c

      if (position === 1) {
        c = this.CircleCalculations.getAttributeCircleInformation()
      } else {
        c = this.CircleCalculations.getSubAttributeCircleInformation()
      }

      let circle = this.canvas.circle(c.d)
        .fill('none')
        .stroke({ width: c.linewidth, dasharray: '0,' + c.l })
        .rotate(-90, this.canvasSize[0] / 2, this.canvasSize[0] / 2)
        .move(c.x, c.y)
        .addClass(className)

      let animateTo = c.l * value

      if (this.hasBeenAnimated === false && speed !== false) {
        // Animate - but only the first time
        circle.animate(speed, '>').stroke({ dasharray: animateTo + ',' + c.l }).afterAll(() => {
          this.hasBeenAnimated = true
        })
      } else {
        circle.stroke({ dasharray: animateTo + ',' + c.l })
      }

      return circle
    },

    addCenterCircle () {
      let c = this.CircleCalculations.getCenterCircleXY()
      let year = this.dataset['ID_year'] ? this.dataset['ID_year'] : 'N/A'
      let dem = this.$store.getters['rsc/getRegimeById'](this.dataset['dem'])
      // let demperf = this.$store.getters['rsc/getRegimePerformanceById'](this.dataset['demperf'])
      let textgroup = this.canvas.group()

      if (this.firstCountry && this.firstCountry.name) {
        textgroup = this.addGroupTextLine(this.firstCountry.name, textgroup)
        textgroup = this.addGroupTextLine(year, textgroup, 'default', 15, 8)
      }

      if (dem && dem.name) {
        // if (parseInt(this.dataset['dem']) === 3 && demperf && demperf.name) {
        //   textgroup = this.addGroupTextLine(demperf.name, textgroup, 'default', 13, 5)
        // }
        textgroup = this.addGroupTextLine(dem.name, textgroup, 'default', 15, 5)
      }

      let circle = this.canvas.circle(c.d)
        .stroke({ width: 0 })
        .move(c.x, c.y)
        .addClass('svg-fill--white')
        .attr('style', 'filter:url(#dropshadow)')

      let dimensions = textgroup.bbox()
      if (dimensions && dimensions.h && dimensions.w) {
        textgroup.move(this.canvasSize[0] / 2, (this.canvasSize[0] / 2) - (dimensions.h / 2))
      }

      this.groups.push(textgroup)

      return circle
    },

    addGroupTextLine (text, group, textWidth = 'default', fontsize = 18, push = 0) {
      let fontstack = 'Yrsa'

      let textBits = splitText(text, textWidth)
      let textObj = this.canvas.text((add) => {
        textBits.forEach((val) => {
          add.tspan(val).newLine()
        })
      })

      textObj.font({
        fill: '#151c2b',
        anchor: 'middle',
        size: fontsize,
        family: fontstack,
        weight: 500
      })

      let dimensions = group.bbox()
      if (dimensions && dimensions.h && dimensions.w) {
        textObj.move(0, dimensions.h + push)
      }

      group.add(textObj)

      return group
    },

    addText (x, y, text, textWidth = 'default', rotation = 0, fontsize = 18) {
      let fontstack = 'Yrsa'

      let textBits = splitText(text, textWidth)
      let textObj = this.canvas.text((add) => {
        textBits.forEach((val) => {
          add.tspan(val).newLine()
        })
      })

      textObj.font({
        fill: '#151c2b',
        anchor: 'middle',
        size: fontsize,
        family: fontstack,
        weight: 500
      })

      let dimensions = textObj.bbox()
      if (dimensions && dimensions.h) {
        y -= (dimensions.h / 2)
        y += (fontsize / 2)
      }

      textObj
        .move(x, y - (fontsize / 2))
        .rotate(rotation)

      return textObj
    },

    addSliceText (text, degree, position = 1) {
      let c = this.CircleCalculations.getLabelInfo(degree, position)

      // Don't write text if there isn't a dataset
      text = this.hasDataset() ? text : ''

      return this.addText(c.x, c.y, text, 'default', c.rotation, 11)
    },

    addCircleLine (degree, position = 1) {
      let c = this.CircleCalculations.getCircleLineInfo(degree, position)

      return this.canvas
        .line(c.x, c.y, c.lx, c.ly)
          .stroke({ width: 2 })
          .addClass('svg-stroke--white')
    },

    getValueColorInfo (val) {
      if (val === undefined || val === false || val === '') {
        return {
          color: 'gray-lightest',
          category: false
        }
      }

      if (val >= 0.4 && val <= 0.7) {
        return {
          color: 'valueyellow',
          category: 'medium'
        }
      }

      if (val > 0.7) {
        return {
          color: 'valuegreen',
          category: 'high'
        }
      }

      return {
        color: 'valuered',
        category: 'low'
      }
    },

    getColorFromPE () {
      if (!this.hasDataset()) return 'gray-lightest'
      let values = []

      this.attributeLabels['A_05'].forEach((attr) => {
        if (this.dataset[attr]) {
          values.push(this.dataset[attr])
        } else {
          console.log('Dataset does not contain a value for ' + attr)
        }
      })

      let minimumValues = values.filter((val) => {
        return val < 0.4
      }).length

      let highValues = values.filter((val) => {
        return val > 0.7
      }).length

      if (minimumValues === 2 && highValues === 2) {
        return 'valuegreen'
      }

      if (minimumValues > 1) {
        return 'valuered'
      }

      if (highValues > 1) {
        return 'valuegreen'
      }

      return 'valueyellow'
    },

    getColorFromValue (val) {
      return this.getValueColorInfo(val).color
    },

    /**
     * Draws the chart object
     *
     * @return  {void}
     */
    drawChart () {
      let group = this.canvas.group()

      // Background circles
      group.add(this.drawCircle(1, 'svg-stroke--gray-lightest', 2))
      group.add(this.drawCircle(1, 'svg-stroke--gray-lightest', 1))

      let attributes1Size = Object.keys(this.attributeLabels).length
      let current1position = 0
      let current2position = 0
      _.each(this.attributeLabels, (attr, key) => {
        let size1Percentage = 1 / attributes1Size
        let color1 = (key === 'A_05') ? this.getColorFromPE() : this.getColorFromValue(this.dataset[key])
        let circle = this.drawCircle(size1Percentage, 'svg-stroke--' + color1, 2, 500)
        let attribute2Size = attr.length
        let label = this.$store.getters['attributes/getLabelFromKey'](key)

        _.each(attr, (subattr) => {
          let size2Percentage = size1Percentage / attribute2Size
          let circle = this.drawCircle(size2Percentage, 'svg-stroke--' + this.getColorFromValue(this.dataset[subattr]), 1, 500)
          let label2 = this.$store.getters['attributes/getLabelFromKey'](subattr)
          this.rotateCircle(circle, current2position)
          group.add(this.addCircleLine(current2position - 90, 2))
          group.add(this.addCircleLine(current2position - 90, 2))
          group.add(circle)

          if (label2) {
            let nextdegree = current2position + 360 * size2Percentage
            let labeldegree = (nextdegree + current2position) / 2
            let text2 = this.addSliceText(label2.name, labeldegree - 90, 2)
            group.add(text2)
          }

          current2position += 360 * size2Percentage
        })

        this.rotateCircle(circle, current1position)
        group.add(this.addCircleLine(current1position - 90, 1))
        group.add(circle)

        if (label) {
          let next1degree = current1position + 360 * size1Percentage
          let label1degree = (next1degree + current1position) / 2
          let text1 = this.addSliceText(label.name, label1degree - 90, 1)
          group.add(text1)
        }

        current1position += 360 * size1Percentage
      })

      group.add(this.addCircleLine(-90, 2))

      group.add(this.addCenterCircle())

      this.groups.push(group)
    },

    /**
     * Initializes the canvas
     *
     * @return  {void}
     */
    drawCanvas () {
      this.canvas = SVG('frameworkwheel-svg--' + this._uid).size(this.widthHeight[0] + 'px', this.widthHeight[1] + 'px')
      this.canvas.viewbox(0, 0, this.canvasSize[0], this.canvasSize[1])
      this.canvas.style({ width: this.widthHeight[0] + 'px', height: this.widthHeight[1] + 'px' })
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '~@/components/sass/shared/colors';

  .frameworkwheel__svg {
    display: block;
    padding: 0;
    margin-bottom: 20px;
  }

</style>



// WEBPACK FOOTER //
// src/components/shared/charts/FrameworkWheel.vue